import { submitContactInquiry, useEtlynCms } from "@etlyn/etlyn-cms";
import React, { useState } from "react";

/* ═══════════════════════════════════════════════════════════ */
/*  CONTACT — corporate split panel: dark info + white form    */
/* ═══════════════════════════════════════════════════════════ */

const budgetOptions = [
  "< $10k",
  "$10k – $25k",
  "$25k – $50k",
  "$50k – $100k",
  "$100k+",
  "Not sure yet",
];

const serviceOptions = [
  "Web Application",
  "Mobile App",
  "UX / UI Design",
  "E-Commerce",
  "SaaS Platform",
  "Other",
];

interface ContactFormProps {
  isMob?: boolean;
}

export function ContactForm({ isMob }: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [values, setValues] = useState({
    name: "",
    company: "",
    email: "",
    service: "",
    budget: "",
    message: "",
  });
  const { state: companyInfoState } = useEtlynCms("company-info");
  const companyInfo = companyInfoState?.data;
  const contactEmail =
    companyInfo?.contact_email && companyInfo.contact_email !== "string"
      ? companyInfo.contact_email
      : "info@etlyn.com";
  const contactPhone =
    companyInfo?.contact_number && companyInfo.contact_number !== "string"
      ? companyInfo.contact_number
      : "+1 415 745 0485";
  const companyLocation =
    companyInfo?.location && companyInfo.location !== "string"
      ? companyInfo.location
      : "San Francisco, CA";

  const set =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setValues((v) => ({ ...v, [field]: e.target.value }));
      if (fieldErrors[field])
        setFieldErrors((prev) => {
          const n = { ...prev };
          delete n[field];
          return n;
        });
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!values.name || values.name.length < 2) errs.name = "Name is required";
    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      errs.email = "Please enter a valid email";
    if (!values.message) errs.message = "Please describe your project";
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const searchParams =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search)
          : new URLSearchParams();

      await submitContactInquiry({
        name: values.name,
        email: values.email,
        company: values.company || undefined,
        message: values.message,
        subject: `Project inquiry from ${values.name}${values.company ? ` (${values.company})` : ""}`,
        source: "contact-form",
        status: "new" as const,
        priority: "medium" as const,
        metadata: {
          service: values.service || undefined,
          budget: values.budget || undefined,
          utm_source: searchParams.get("utm_source") || undefined,
          utm_medium: searchParams.get("utm_medium") || undefined,
          utm_campaign: searchParams.get("utm_campaign") || undefined,
          referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
        },
      });

      setIsSubmitted(true);
      setValues({ name: "", company: "", email: "", service: "", budget: "", message: "" });
    } catch {
      setError(`Something went wrong. Please email us directly at ${contactEmail}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-[#DFDFE0] bg-white px-4 py-3 text-sm text-[#333D51] placeholder:text-[#808692]/60 outline-none transition-all focus:border-[#0D2B7E]/40 focus:ring-2 focus:ring-[#0D2B7E]/10 disabled:opacity-50";
  const selectCls =
    "w-full appearance-none rounded-lg border border-[#DFDFE0] bg-white px-4 py-3 text-sm text-[#333D51] outline-none transition-all focus:border-[#0D2B7E]/40 focus:ring-2 focus:ring-[#0D2B7E]/10 disabled:opacity-50 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22none%22%20stroke%3D%22%23808692%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m2%204%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_14px_center] bg-no-repeat pr-10";
  const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#808692]";
  const errCls = (field: string) => (fieldErrors[field] ? " !border-red-300 !ring-red-100" : "");

  /* ── Success state ─────────────────────────────────────── */
  if (isSubmitted) {
    return (
      <section id="contact" className="mb-16">
        <div className="overflow-hidden rounded-2xl ring-1 ring-[#DFDFE0]/60">
          <div className="flex min-h-[420px] items-center justify-center bg-white p-12 text-center">
            <div>
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#28E454]/10">
                <svg className="h-8 w-8 text-[#28E454]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#333D51] tracking-tight">
                Thank you for reaching out
              </h3>
              <p className="mt-2 text-sm text-[#666D7C] leading-relaxed max-w-sm mx-auto">
                We have your brief and will get back within one business day with clear next steps, timing, and a budget range.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-lg border border-[#DFDFE0] px-5 py-2.5 text-sm font-medium text-[#333D51] transition hover:bg-[#F8F9FB]"
              >
                Submit another inquiry
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ── Main layout: dark panel + form ────────────────────── */
  return (
    <section id="contact" className="mb-16">
      <div className="overflow-hidden rounded-2xl ring-1 ring-[#DFDFE0]/60">
        <div className="grid lg:grid-cols-5">
          {/* ── Left: dark info panel ──────────────────────── */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#0D2B7E] to-[#1A3486] p-8 sm:p-10 lg:col-span-2">
            {/* Grid pattern overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.2) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* Radial glow */}
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#50BCFF]/[0.08] blur-3xl" />
            <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-white/[0.03] blur-2xl" />

            <div className="relative flex h-full flex-col justify-between">
              <div>
                <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#50BCFF] mb-5">
                  Get in touch
                </span>
                <h2 className="text-2xl font-bold text-white tracking-tight leading-snug mb-3">
                  Let&apos;s discuss<br />your next project
                </h2>
                <p className="text-sm leading-relaxed text-white/50 max-w-xs mb-10">
                  Share the details and our team will prepare a tailored proposal with scope, timeline, and budget.
                </p>

                {/* Contact details */}
                <div className="space-y-4 mb-10">
                  <a href={`mailto:${contactEmail}`} className="flex items-center gap-3 text-sm text-white/60 transition hover:text-white/90 group">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.06] ring-1 ring-white/[0.08] transition group-hover:bg-white/10">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    {contactEmail}
                  </a>
                  <a href={`tel:${contactPhone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-3 text-sm text-white/60 transition hover:text-white/90 group">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.06] ring-1 ring-white/[0.08] transition group-hover:bg-white/10">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                      </svg>
                    </div>
                    {contactPhone}
                  </a>
                  <div className="flex items-start gap-3 text-sm text-white/60">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] ring-1 ring-white/[0.08]">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                      </svg>
                    </div>
                    <span className="pt-2">{companyLocation}</span>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="space-y-3">
                <div className="h-px bg-white/[0.08]" />
                <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
                  {[
                    { num: "50+", label: "Projects" },
                    { num: "99.9%", label: "Uptime" },
                    { num: "3×", label: "Faster" },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-baseline gap-1.5">
                      <span className="text-sm font-bold text-white">{stat.num}</span>
                      <span className="text-[11px] text-white/40">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: form ────────────────────────────────── */}
          <div className="bg-white p-8 sm:p-10 lg:col-span-3">
            <div className="mb-7">
              <h3 className="text-lg font-bold text-[#333D51] tracking-tight">
                Tell us about your project
              </h3>
              <p className="mt-1 text-sm text-[#808692]">
                Fields marked with <span className="text-red-400">*</span> are required
              </p>
            </div>

            {error && (
              <div className="mb-5 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* Row 1: Name + Company */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>
                    Full name <span className="text-red-400">*</span>
                  </label>
                  <input
                    className={inputCls + errCls("name")}
                    placeholder="John Smith"
                    disabled={isSubmitting}
                    value={values.name}
                    onChange={set("name")}
                  />
                  {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
                </div>
                <div>
                  <label className={labelCls}>Company</label>
                  <input
                    className={inputCls}
                    placeholder="Acme Inc."
                    disabled={isSubmitting}
                    value={values.company}
                    onChange={set("company")}
                  />
                </div>
              </div>

              {/* Row 2: Email + Service */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>
                    Work email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    className={inputCls + errCls("email")}
                    placeholder="john@acme.com"
                    disabled={isSubmitting}
                    value={values.email}
                    onChange={set("email")}
                  />
                  {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
                </div>
                <div>
                  <label className={labelCls}>Service needed</label>
                  <select
                    className={selectCls + (values.service ? "" : " text-[#808692]/60")}
                    disabled={isSubmitting}
                    value={values.service}
                    onChange={set("service")}
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    {serviceOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Budget */}
              <div>
                <label className={labelCls}>Estimated budget</label>
                <div className="flex flex-wrap gap-2">
                  {budgetOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => setValues((v) => ({ ...v, budget: v.budget === opt ? "" : opt }))}
                      className={`rounded-lg border px-3.5 py-2 text-xs font-medium transition ${
                        values.budget === opt
                          ? "border-[#0D2B7E] bg-[#0D2B7E]/[0.04] text-[#0D2B7E] ring-1 ring-[#0D2B7E]/20"
                          : "border-[#DFDFE0] text-[#666D7C] hover:border-[#0D2B7E]/30 hover:text-[#333D51]"
                      } disabled:opacity-50`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 4: Message */}
              <div>
                <label className={labelCls}>
                  Project details <span className="text-red-400">*</span>
                </label>
                <textarea
                  className={`${inputCls} min-h-[100px] resize-y${errCls("message")}`}
                  placeholder="Describe your project goals, target audience, key features, and any timeline considerations..."
                  disabled={isSubmitting}
                  rows={4}
                  value={values.message}
                  onChange={set("message")}
                />
                {fieldErrors.message && <p className="mt-1 text-xs text-red-500">{fieldErrors.message}</p>}
              </div>

              {/* Submit */}
              <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#0D2B7E] to-[#25408B] px-7 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Request a proposal
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                      </svg>
                    </>
                  )}
                </button>
                <p className="text-[11px] text-[#808692] sm:text-right sm:max-w-[200px] leading-relaxed">
                  We respond within 24 hours. Your data is protected under our{" "}
                  <span className="text-[#0D2B7E]/70 underline-offset-2 hover:underline cursor-pointer">
                    Privacy Policy
                  </span>.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
